export enum Mode {
  Home = 'home',
  Scale = 'scale',
  Measure = 'measure',
  Linear = 'linear',
  Draw = 'draw',
  FreeHand = 'freeHand',
  Highlighter = 'highlighter',
  Pen = 'pen',
  Text = 'text',
  Pieces = 'pieces',
  Selector = 'selector',
  Cloud = 'cloud',
  Counter = 'counter',
  Notes = 'notes',
  DrawingSelectionArea = 'drawingSelectionArea',
  TransformTool = 'transformTool',
  CutOutSection = 'cutOutSection',
  AutoTake = 'autoTake',
  Undo = 'undo',
  Redo = 'redo',
  Stop = 'stop',
  Comment = 'comment',
  Area = 'area',
  Materials = 'materials',
  PageOptions = 'pageOptions',
  HelpMenu = 'helpMenu',
  UndoRedo = 'undoRedo',
  Hotkeys = 'hotkeys',
  Shortcuts = 'shortcuts',
  Markup = 'markup',
  BoxMode = 'boxMode',
  Orthogonal = 'orthogonal',
  Snapping = 'snapping',
  DrawingControls = 'drawingControls',
  Chat = 'chat',
}

// Drawing needs a clear canvas: the sidebar and the assistant both hide while one of
// these is active. Measure covers the Check scale flow — the verify card needs the canvas too.
export const isDrawingMode = (mode: Mode | null | undefined): boolean =>
  mode === Mode.Area || mode === Mode.Linear || mode === Mode.Counter || mode === Mode.Scale || mode === Mode.Measure

export const ShapeType = {
  COUNT: 'COUNT',
  LINE: 'LINE',
  LINEAR_LINE: 'LINEAR_LINE',
  MEASURED_LINE: 'MEASURED_LINE',
  POLYGON: 'POLYGON',
  RECTANGLE: 'RECTANGLE',
  FREE_HAND_SHAPE: 'FREE_HAND_SHAPE',
  PEN: 'PEN_SHAPE',
  CLOUD: 'CLOUD_SHAPE',
  TEXT: 'TEXT_SHAPE',
  VOLUME: 'VOLUME_POLYGON',
  PITCH: 'PITCHED_POLYGON',
  CALLOUT: 'CALLOUT_SHAPE',
} as const
export type ShapeType = (typeof ShapeType)[keyof typeof ShapeType]

// Area shapes persist under three shapeType values; every "is this a polygon" filter must accept all of them.
export const POLYGON_SHAPE_TYPES: readonly string[] = [ShapeType.POLYGON, ShapeType.VOLUME, ShapeType.PITCH]
export const isPolygonShapeType = (shapeType?: string): boolean => !!shapeType && POLYGON_SHAPE_TYPES.includes(shapeType)

export const UnitSystem = {
  METRIC: 'METRIC',
  IMPERIAL: 'IMPERIAL',
} as const
export type UnitSystem = (typeof UnitSystem)[keyof typeof UnitSystem]

export enum AreaModeType {
  Box = 'box',
  Polygon = 'polygon',
  //PolygonSnap = 'polygonSnap',
}

export enum SidebarEditContent {
  Folders = 'folders',
  Pages = 'pages',
}

export enum FoldersTypes {
  Folder = 'folder',
  SubFolder = 'subFolder',
}

export enum Modals {
  ScaleModal = 'scaleModal',
  ScaleInfoModal = 'scaleInfoModal',
  AreaAddModal = 'areaAddModal',
  CountModal = 'countModal',
  LinearLineModal = 'linearLineModal',
  MaterialTradeModal = 'materialsTradeModal', // Trade contains categories. Trade is a company making specific part of building
  MaterialTradeCategoryModal = 'materialsCategoryTradeModal', // category contains materials
  MaterialModal = 'materialsModal',
  ManageProjectMaterials = 'manageMaterials',
  MaterialsSelectionToProject = 'materialsSelectionToProject',
  InviteToProject = 'inviteToProject',
  MaterialSwitch = 'materialSwitch',
  RunAutoTakeModal = 'runAutoTakeModal',
  AssemblyModal = 'assemblyModal',
  CreateAssemblyModal = 'createAssemblyModal',
}

export enum LoginFormTypes {
  LogIn = 'LogIn',
  SignUp = 'SignUp',
  ForgotPassword = 'ForgotPassword',
}

export enum ColorTypes {
  main = '#F5F8FD',
  dark = '#2C2C2C',
  light = '#DBE1FF',
  contrastText = '#FFFFFF',
  blue = '#2482D8',
  darkGray = '#F9FAFB',
  lightGray = '#C0C0C0',
  unclassified = '#2482D8',
  // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
  blueOrchid = '#2F80ED',
  hover = '#2482D8',
  font = '#EBF1F7',
  icon = '#536976',
  high = '#D0E4F7',
  stroke = '#E8EAE6',
  // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
  white = '#FFFFFF',
  background = '#636F7E',
  error = '#EB5757',
  secondary = '#D2D3D4',
  text = '#2C2C2C',
  focus = '#185EA3',
  selected = '#EBF1F7',
}

export enum LocalStorageNames {
  WalkThroughApp = 'WalkThroughApp',
}

export enum WalkThroughAppSteps {
  Sheets = 'Sheets',
  ScaleOpen = 'ScaleOpen',
  ScaleSelect = 'ScaleSelect',
  ScaleConfirm = 'ScaleConfirm',
  ToolPicker = 'ToolPicker',
  PlacePoints = 'PlacePoints',
  DrawingControls = 'DrawingControls',
  DrawName = 'DrawName',
  SmartScope = 'SmartScope',
  QuantitiesDone = 'QuantitiesDone',
  ProposalScopes = 'ProposalScopes',
  ProposalDisplay = 'ProposalDisplay',
  ProposalNoScopes = 'ProposalNoScopes',
  ProposalSaveVersion = 'ProposalSaveVersion',
  AssistantReopen = 'AssistantReopen',
}

export const UploadFileOption = {
  ALL_INTO_FOLDERS: 'uploadAllIntoFolders',
  WITHOUT_FOLDERS: 'uploadWithoutFolders',
  FOLDER: 'uploadFolder',
} as const
export type UploadFileOption = (typeof UploadFileOption)[keyof typeof UploadFileOption]
